// deps
import React, { ReactElement, useCallback, useRef, RefObject } from "react";
import { Portal } from "@swiggy-private/core-ui";
import { useEffectOnce } from "@swiggy-private/core-ui";

import styles from "./Tooltip.scss";
import { TooltipContent, TooltipGradient } from "./styles";

interface TooltipProps {
    hide: VoidFunction;
    position: {
        x: number;
        y: number | string;
    };
    widgetSize: string;
    triggerRef: RefObject<HTMLElement | null>;
    backgroundGradient?: string;
    children?: ReactElement | ReactElement[];
}

const TOOLTIP_DEFAULTS = {
    ARROW_LEFT_POSITION: 28,
    PADDING: 16,
    AFTER_LEFT_POSITION_OFFSET: 4,
};

const Tooltip = ({
    hide,
    position,
    widgetSize,
    triggerRef,
    children,
    backgroundGradient,
}: TooltipProps): ReactElement => {
    const _tooltipRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const timer = useRef(0);

    const _getToolTipXOffset = useCallback(() => {
        if (typeof window !== "object") return 0;

        const { x } = position;
        const width = Number(widgetSize);
        if (x < TOOLTIP_DEFAULTS.PADDING * 2) {
            return -TOOLTIP_DEFAULTS.PADDING;
        } else if (x + width + TOOLTIP_DEFAULTS.PADDING > window.innerWidth) {
            return Math.abs(window.innerWidth - x - TOOLTIP_DEFAULTS.PADDING - width);
        }

        return 0;
    }, [position, widgetSize]);

    const getTooltipPositionStyles = useCallback(() => {
        const { x, y } = position;

        const xOffset = _getToolTipXOffset();
        return {
            transform: `translate(${x - xOffset}px, ${y}px) translateY(-100%)`,
            webkitTransform: `translate(${x - xOffset}px, ${y}px) translateY(-100%)`,
            width: `${widgetSize}px`,
        };
    }, [position, widgetSize, _getToolTipXOffset]);

    const _getArrowPositionStyles = useCallback(
        (offset = 0) => {
            const left = TOOLTIP_DEFAULTS.ARROW_LEFT_POSITION + _getToolTipXOffset();
            return { left: `${left - offset}px` };
        },
        [_getToolTipXOffset],
    );

    const onClick = useCallback(
        (event: MouseEvent) => {
            if (
                event.target == null ||
                _tooltipRef.current === null ||
                _tooltipRef.current === event.target ||
                (event.target instanceof Node && _tooltipRef.current.contains(event.target))
            ) {
                return;
            }

            hide();
        },
        [_tooltipRef, hide],
    );

    const onParentScroll = useCallback(() => {
        window.cancelAnimationFrame(timer.current);
        timer.current = window.requestAnimationFrame(() => hide());
    }, [hide]);

    const getScrollParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
        if (!element) {
            return null;
        }

        let parent = element.parentElement;
        while (parent) {
            const { overflowY } = window.getComputedStyle(parent);
            if (overflowY === "scroll") {
                return parent;
            }
            parent = parent.parentElement;
        }

        return document.documentElement;
    }, []);

    useEffectOnce(() => {
        document.addEventListener("click", onClick, false);
        const scrollParent = getScrollParent(triggerRef?.current);
        if (scrollParent == null) {
            return;
        }

        scrollParent.addEventListener("scroll", onParentScroll, false);

        return (): void => {
            window.cancelAnimationFrame(timer.current);
            document.removeEventListener("click", onClick, false);

            const scrollParent = getScrollParent(triggerRef?.current);
            if (scrollParent == null) {
                return;
            }

            scrollParent.removeEventListener("scroll", onParentScroll, false);
        };
    });

    return (
        <Portal>
            <div className={styles.tooltip} ref={_tooltipRef} style={getTooltipPositionStyles()}>
                <div className={styles.tooltipBefore} style={_getArrowPositionStyles()} />
                <TooltipContent>{children}</TooltipContent>
                <div
                    className={styles.tooltipAfter}
                    style={_getArrowPositionStyles(TOOLTIP_DEFAULTS.AFTER_LEFT_POSITION_OFFSET)}
                />
                {backgroundGradient && <TooltipGradient gradientColor={backgroundGradient} />}
            </div>
        </Portal>
    );
};

export default Tooltip;
